import { cloneDeep } from 'lodash';
import { StoreId } from 'src/app/shared/config/store-config';

// Form state of the marketplace receipt page. Everything the operator can see
// and edit before rendering lives here; the order fetched from the platform
// is flattened into it (not kept as raw platform JSON) so the form is the
// single thing the builder reads.
export interface ReceiptNoteInput {
    source: SourceInfo;
    docInfo: DocInfo;
    customerInfo: CustomerInfo;
    amounts: AmountInfo;
    items: ReceiptItemInfo[];
}

interface SourceInfo {
    // Nullable because the select box has a clear button; loading guards
    // against a missing store.
    storeId: StoreId | null;
    orderNo: string;
}

export interface DocInfo {
    // The receipt date (YYYY-MM-DD). Pre-filled from the order's payment time
    // when the platform reports one, else its creation time.
    date: string;
    remark: string;
    customTitle: string;
    // Stamps the stored shop signature (the shop owner's own). On by default
    // for every store — the operator issuing the receipt is the signer — and
    // switched off for a receipt that is to be signed by hand.
    showSellerSignature: boolean;
    hideLogo: boolean;
    // Prints the date row — and the date under a stamped signature — blank,
    // for a receipt handed over for the customer to date themselves. `date`
    // is kept as loaded, so turning this back off restores it.
    hideDate: boolean;
}

interface CustomerInfo {
    name: string;
    taxId: string;
    branch: string;
    address: string;
}

// Discount and shipping are the two amounts the platform reports at order
// level; the item total and the net price are derived from them and the item
// rows, never entered.
interface AmountInfo {
    discount: number;
    shippingCost: number;
}

export interface ReceiptItemInfo {
    itemNo: number;
    description: string;
    unitPrice: number;
    quantity: number;
    amount: number;
}

// What the page knows about the order it last loaded, for the banner and for
// seller resolution at the order's date. Cleared by the clear button and
// replaced by the next load.
export interface LoadedOrderInfo {
    storeId: StoreId;
    platform: 'lazada' | 'shopee' | 'tiktok';
    orderNo: string;
    // ISO date (YYYY-MM-DD) the order was created, or '' when the platform
    // gave none. Drives sellerFor: on a store that changed taxpayer, the
    // order date decides whose sale it was.
    createdIsoDate: string;
    // Where the customer block came from: the customer's own billing request
    // (legal name, tax id, address) or the platform's recipient fields, which
    // Shopee and TikTok mask.
    customerSource: 'billing-request' | 'platform';
    paymentMethod: string;
}

const inputMomentFormat = 'YYYY-MM-DD';

const initialReceiptNoteInput: ReceiptNoteInput = {
    source: {
        storeId: null,
        orderNo: '',
    },
    docInfo: {
        date: '',
        remark: '',
        customTitle: '',
        showSellerSignature: true,
        hideLogo: false,
        hideDate: false,
    },
    customerInfo: {
        name: '',
        taxId: '',
        branch: '',
        address: '',
    },
    amounts: {
        discount: 0,
        shippingCost: 0,
    },
    items: [],
};

const getInitialReceiptNoteInput = (): ReceiptNoteInput => {
    return cloneDeep(initialReceiptNoteInput);
};

export { inputMomentFormat, getInitialReceiptNoteInput };
